import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { whatsappLink } from '../lib/whatsapp';
import { useLanguage } from '../lib/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';
export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const {
    t,
    lang
  } = useLanguage();
  const links = [{
    label: t('nav.home'),
    href: '#inicio'
  }, {
    label: t('nav.experiences'),
    href: '#experiencias'
  }, {
    label: t('nav.gallery'),
    href: '#galeria'
  }, {
    label: t('nav.reviews'),
    href: '#opiniones'
  }, {
    label: t('nav.contact'),
    href: '#contacto'
  }];
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  const handleClick = (href: string) => {
    setOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 60);
  };
  const leftLinks = [links[1], links[2]];
  const rightLinks = [links[3], links[4]];
  return <header className={`sticky top-0 z-40 transition-all duration-500 w-full ${scrolled ? 'bg-ivory/95 backdrop-blur-md border-b border-beige shadow-sm' : 'bg-sand border-b border-transparent'}`} data-id="element-230">
      <div className="py-4 lg:py-6 px-5 sm:px-8 lg:px-12 flex items-center justify-between uppercase text-[10px] tracking-[0.2em] font-medium text-volcanic" data-id="element-231">
        <nav className="hidden lg:flex gap-10 flex-1" data-id="element-232">
          {leftLinks.map(l => <a key={l.href} href={l.href} onClick={e => {
          e.preventDefault();
          handleClick(l.href);
        }} className="hover:text-terracotta transition-colors duration-300 cursor-pointer" data-id="element-233">
              {l.label}
            </a>)}
        </nav>

        <button onClick={() => setOpen(o => !o)} aria-label={open ? 'Cerrar menú' : 'Abrir menú'} className="lg:hidden text-volcanic p-2 -ml-2 hover:text-terracotta transition-colors z-50 relative" data-id="element-234">
          {open ? <XIcon size={24} strokeWidth={1.5} data-id="element-235" /> : <MenuIcon size={24} strokeWidth={1.5} data-id="element-236" />}
        </button>

        <div className="flex items-center gap-6" data-id="element-237">
          <a href="#inicio" onClick={e => {
          e.preventDefault();
          handleClick('#inicio');
        }} className="text-center cursor-pointer select-none flex flex-col items-center group relative z-50" data-id="element-238">
            <div className="flex items-center gap-2 sm:gap-3 mb-1" data-id="element-239">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="group-hover:scale-105 transition-transform duration-500 sm:w-6 sm:h-6" data-id="element-240">
                <circle cx="12" cy="10" r="6" fill="#C97B63" fillOpacity="0.9" data-id="element-241" />
                <path d="M4 16C6 16 7 14 10 14C13 14 14 16 17 16C20 16 21 14 23 14" stroke="#7B8B6F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-242" />
                <path d="M2 19C4 19 5 17 8 17C11 17 12 19 15 19C18 19 19 17 21 17" stroke="#7B8B6F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" data-id="element-243" />
              </svg>
              <h1 className="font-display text-volcanic text-lg sm:text-xl lg:text-2xl font-normal tracking-[0.18em] leading-none whitespace-nowrap" data-id="element-244">
                YES PICNIC TENERIFE
              </h1>
            </div>
            <p className="font-script text-terracotta text-base sm:text-lg lg:text-xl capitalize tracking-normal mt-[-4px]" data-id="element-245">
              Tenerife · luxury picnics
            </p>
          </a>
          <div className="hidden lg:block" data-id="element-246">
            <LanguageSwitcher data-id="element-247" />
          </div>
        </div>

        <div className="hidden lg:flex flex-1 justify-end items-center gap-10" data-id="element-248">
          <nav className="flex gap-10" data-id="element-249">
            {rightLinks.map(l => <a key={l.href} href={l.href} onClick={e => {
            e.preventDefault();
            handleClick(l.href);
          }} className="hover:text-terracotta transition-colors duration-300 cursor-pointer" data-id="element-250">
                {l.label}
              </a>)}
          </nav>
          <a href={whatsappLink('navbar', lang)} target="_blank" rel="noopener noreferrer" className="bg-terracotta hover:bg-terracottaDark text-ivory px-7 py-3 rounded-md uppercase tracking-[0.2em] text-[10px] transition-all duration-300" data-id="element-251">
            {t('nav.book')}
          </a>
        </div>

        <div className="lg:hidden w-8 flex justify-end" data-id="element-252">
          <LanguageSwitcher data-id="element-253" />
        </div>
      </div>

      <AnimatePresence data-id="element-254">
        {open && <motion.div initial={{
        opacity: 0,
        y: '-100%'
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: '-100%'
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="lg:hidden fixed inset-0 bg-ivory z-40 flex flex-col pt-24 pb-12 px-6 overflow-y-auto" data-id="element-255">
            <nav className="flex flex-col items-center justify-center gap-8 flex-1 min-h-min" data-id="element-256">
              {links.map((l, i) => <motion.a key={l.href} href={l.href} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + i * 0.1,
            duration: 0.6,
            ease: 'easeOut'
          }} onClick={e => {
            e.preventDefault();
            handleClick(l.href);
          }} className="font-display text-volcanic text-4xl font-light hover:text-terracotta transition-colors duration-300" data-id="element-257">
                  {l.label}
                </motion.a>)}
              <motion.a initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.6,
            ease: 'easeOut'
          }} href={whatsappLink('navbar', lang)} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="mt-8 bg-terracotta hover:bg-terracottaDark text-ivory px-10 py-4 rounded-md uppercase tracking-[0.2em] text-[11px] transition-colors duration-300 w-full max-w-xs text-center" data-id="element-258">
                {t('nav.bookNow')}
              </motion.a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}